import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'tr/hedefli-trafik-satin-al',
    titleTag: 'Buy Targeted Traffic | Country & Niche Targeting | Traffic Creator',
    metaDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience. Verified in GA4.',
    keywords: 'Buy Targeted Traffic, Buy Targeted Website Traffic, Buy Targeted Web Traffic, Targeted Traffic That Converts, Buy Keyword Targeted Traffic',
    primaryKeyword: 'Buy Targeted Traffic',
    secondaryKeyword: 'Targeted Traffic That Converts',
    reviewCount: '518',
    ratingValue: '4.9',

    h1: 'Buy Targeted Traffic That Converts into Real Results',
    subheadline: 'Generic traffic is wasted budget. Our platform lets you buy targeted traffic from exact countries, devices, and referrer paths — matching your ideal audience profile perfectly.',

    problemH2: 'Why Untargeted Traffic Wastes Your Budget',
    problemText: 'Sending random visitors from irrelevant countries and devices to your website creates terrible bounce rates and zero conversions. You need traffic that matches your actual audience — the right geography, the right device, and the right browsing behavior.',
    solutionPoints: [
        'Precision geo-targeting from 190+ countries down to city level',
        'Device targeting — desktop, mobile, or tablet traffic on demand',
        'Custom referrer paths to simulate traffic from Google, social media, or direct',
        'Keyword-targeted sessions that arrive via search-like pathways'
    ],

    featuresH2: 'How Our Targeted Traffic Platform Works',
    features: [
        { icon: <Globe />, title: 'Country & City Targeting', description: 'Choose traffic from any country worldwide. Need visitors specifically from New York, London, or Tokyo? Our residential network delivers with pinpoint accuracy.' },
        { icon: <BarChart3 />, title: 'Behavioral Targeting', description: 'Configure exactly how visitors interact — dwell time, pages per session, scroll depth, and bounce rate — to match your niche\'s natural engagement patterns.' },
        { icon: <Activity />, title: 'Referrer Source Control', description: 'Make traffic appear as if it came from Google search, social media, or direct visits. Full control over the referrer path for realistic Analytics data.' }
    ],

    testimonialsH2: 'What Our Targeted Traffic Clients Say',
    testimonials: [
        { name: 'David L.', role: 'E-commerce Director', result: 'USA Traffic +400%', text: 'We needed US-only traffic for our Shopify store. The geo-targeting accuracy was incredible — 98% of visits came from our target states.', seed: 'davidl' },
        { name: 'Nina S.', role: 'Media Buyer', result: 'Perfect Device Mix', text: 'Being able to buy targeted traffic split 70/30 mobile/desktop matched our actual audience perfectly. The Analytics looked completely natural.', seed: 'ninas' },
        { name: 'Carlos M.', role: 'Local Business Owner', result: 'City-Level Precision', text: 'I bought keyword targeted traffic for my local service area. All visits came from my city and neighboring areas. Incredible precision.', seed: 'carlosm' }
    ],

    ctaH2: 'Ready to Buy Targeted Traffic That Matches Your Audience?',

    faqs: [
        { question: 'What does buy targeted traffic mean exactly?', answer: 'Buying targeted traffic means purchasing website visitors that match specific criteria — geographic location, device type, referrer source, and behavioral patterns. Unlike generic traffic, targeted traffic aligns with your actual audience profile.' },
        { question: 'Can I buy targeted traffic from a specific country?', answer: 'Yes! We offer geo-targeting from 190+ countries. You can target by country, region, or even city level. Our residential IP network ensures every visit genuinely originates from your chosen location.' },
        { question: 'Does buying targeted traffic that converts actually work?', answer: 'The traffic itself generates realistic engagement signals (dwell time, scrolling, page views) that improve your site\'s SEO metrics. For e-commerce conversions, it helps by improving your search rankings which leads to more genuine converting visitors.' },
        { question: 'How is keyword targeted traffic different?', answer: 'Keyword targeted traffic arrives at your site through search-like pathways, mimicking visitors who found you through specific search queries. This creates organic search signals that can improve your rankings for those exact keywords.' },
        { question: 'Will targeted traffic appear in Google Analytics?', answer: 'Yes, 100%. All targeted traffic registers in GA4 with accurate geographic data, device information, session duration, and referrer paths — exactly matching the targeting parameters you set.' }
    ],

    relatedLinks: [
        { label: 'Geo-Targeted Traffic', to: '/tr/hedefli-trafik' },
        { label: 'Buy Website Traffic', to: '/tr/web-sitesi-trafigi-satin-al' },
        { label: 'Local SEO Traffic Bot', to: '/tr/yerel-seo-trafik-botu' }
    ],

    productName: 'Buy Targeted Traffic - Precision Audience Matching',
    productDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience.',
    translations: {
        'navFeatures': 'Ozellikler',
        'navPricing': 'Fiyatlar',
        'navFaq': 'SSS',
        'navLogin': 'Panel Girisi',
        'heroBadge': 'Premium Trafik Hizmeti',
        'heroCtaPrimary': 'Ucretsiz Deneyin',
        'heroCtaSecondary': 'Fiyatlari Gorun',
        'heroTrust': '10.000+ kullanici tarafindan guveniliyor',
        'heroMicrocopy': 'Kredi karti gerekmez. %100 guvenli & Analytics ile dogrulanmis.',
        'heroLiveLabel': 'Canli',
        'heroTrafficIncrease': 'Trafik Artisi',
        'heroActiveLabel': 'Aktif',
        'heroVisitorsServed': 'Sunulan Ziyaretciler',
        'socialProofLabel': 'Guvenilen:',
        'problemWithout': 'Olmadan',
        'problemWith': 'Ile',
        'problemBullets': [
                'Dusuk organik trafik buyumenizi sinirlar',
                'Kotu arama siralamalari donusumlere zarar verir',
                'Rakipler anahtar kelimelerinize hakim',
                'Yuksek hemen cikma oranlari SEO zarar verir'
        ],
        'statsCampaigns': 'Gunluk Aktif Kampanyalar',
        'statsCountries': 'Ulke',
        'statsUptime': 'Calisma Suresi',
        'statsSupport': 'Destek',
        'featuresSubtitle': 'Web sitenizin performansini artirmak icin ihtiyaciniz olan her sey.',
        'howItWorksH2': '3 Basit Adim',
        'howItWorksSteps': [
                {
                        'title': 'Kampanya Yapilandirin',
                        'desc': 'Ulke, cihaz ve davranis ayarlarini dakikalar icinde secin.'
                },
                {
                        'title': 'Agi Baslatin',
                        'desc': 'Platformumuz gercek ziyaretcileri URL adresinize yonlendirir.'
                },
                {
                        'title': 'Sonuclari Analiz Edin',
                        'desc': 'Google Analytics 4 de metriklerinizin iyilestigini izleyin.'
                }
        ],
        'pricingH2': 'Seffaf',
        'pricingSubtitle': 'Tek seferlik alisveri. Abonelik yok. Krediler asla bitmez.',
        'pricingMostPopular': 'En Populer',
        'pricingVisitors': 'ziyaretci',
        'pricingGetStarted': 'Hemen Baslayin',
        'pricingGuarantee': '30 Gun Para Iade Garantisi',
        'pricingDelivery': 'Aninda Teslimat (~5 dk)',
        'pricingCountries': '190+ Ulke',
        'faqH2': 'Sikca Sorulan Sorular',
        'ctaSubtitle': '10.000+ musteriye katilin ve bugun trafiginizi artirin.',
        'ctaButtonPrimary': 'Ucretsiz Basla',
        'ctaButtonSecondary': 'Fiyatlari Gor',
        'ctaMicrocopy': 'Kredi karti gerekmez. %100 guvenli & Analytics ile dogrulanmis.',
        'footerDescription': 'dunya genelinde SEO profesyonelleri ve dijital ajanslar icin cozumler.',
        'footerRelatedServices': 'Ilgili Hizmetler',
        'footerCompany': 'Sirket',
        'footerAbout': 'Hakkimizda',
        'footerBlog': 'Blog',
        'footerHelpCenter': 'Yardim Merkezi',
        'footerPrivacy': 'Gizlilik Politikasi',
        'footerImpressum': 'Yasal Bilgi',
        'footerStatus': 'Tum Sistemler Calisir Durumda',
        'footerBuyTraffic': 'Web Sitesi Trafigi Satin Al'
},
};

const TrBuyTargetedTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default TrBuyTargetedTraffic;
